import { call, put, select, takeEvery, takeLatest, take, all } from 'redux-saga/effects'

import { SearchFields, ExcelMimeType } from 'static/constants'

import * as Actions from 'actions/influencers'
import * as AppActions from 'actions/app'
import * as ContactManagementActions from 'actions/contact_management'
import * as Api from 'api/bff'
import * as JournlistsApi from 'api/journalists'
import * as Selectors from 'selectors'
import { exception } from 'actions/app'
import { uiAddFilters } from 'actions/ui'
import { setNewsView, newsRequestSuccess } from 'actions/news'
import { retriable, downloadBlob } from 'utils/sagas'

export function* fetchInfluencersTry() {
  const body = yield select(Selectors.getInfluencersSearchBody)

  const result = yield call(Api.searchInfluencers, body)

  yield put(Actions.influencersRequestSuccess(result))
}

export function* fetchInfluencersFail(error) {
  yield put(Actions.influencersRequestError(error))
  yield put(exception(error))
}

export function* fetchInfluencers() {
  yield call(retriable, fetchInfluencersTry, fetchInfluencersFail)
}

export function* fetchMoreInfluencersTry() {
  const from = yield select(Selectors.getInfluencersFrom)

  if (from) {
    const searchBody = yield select(Selectors.getInfluencersSearchBody)
    const body = { ...searchBody, from }
    const result = yield call(Api.searchInfluencers, body)
    yield put(Actions.moreInfluencersRequestSuccess(result))
  }
}

export function* fetchMoreInfluencersFail(error) {
  yield put(Actions.moreInfluencersRequestError(error))
  yield put(exception(error))
}

export function* fetchMoreInfluencers() {
  yield call(retriable, fetchMoreInfluencersTry, fetchMoreInfluencersFail)
}

export function* exportInfluencersXlsx() {
  try {
    const body = yield select(Selectors.getInfluencersSearchBody)
    const result = yield call(Api.searchInfluencersXlsx, body)

    yield call(downloadBlob, result, 'influencers.xlsx', ExcelMimeType)
    yield put(Actions.exportInfluencersXlsxSuccess())
  } catch (error) {
    yield put(Actions.exportInfluencersXlsxError(error))
    yield put(exception(error))
  }
}

export function* fetchInfluencerTry({ payload: id }) {
  const result = yield call(Api.fetchInfluencer, id)

  yield put(Actions.fetchInfluencerRequestSuccess(result))
}

export function* fetchInfluencerFail(error) {
  yield put(Actions.fetchInfluencerRequestError(error))
  yield put(exception(error))
}

export function* fetchInfluencer(action) {
  yield call(retriable, fetchInfluencerTry, fetchInfluencerFail, action)
}

export function* searchForInfluencer({ payload: { influencer, targetView, groupType = null } }) {
  const searchOptions = [{
    filter: influencer,
    field: SearchFields.INFLUENCERS
  }]

  if (groupType) {
    const groups = yield select(Selectors.getNewsGroups)
    searchOptions.push({
      filter: groups.find(gr => gr.get('name').toLowerCase() === groupType.toLowerCase()),
      field: SearchFields.CHANNELS
    })
  }

  yield put(uiAddFilters(searchOptions))
  yield take(newsRequestSuccess)
  yield put(setNewsView(targetView))
}

export function* setSortBy() {
  yield put(Actions.influencersRequestStart())
}

export function* showInfluencerDetails({ payload: influencer }) {
  yield put(Actions.fetchInfluencerRequestStart(influencer.get('id')))
}

export function* selectChannel() {
  const channelId = yield select(Selectors.getInfluencersDetailsSelectedChannelId)
  yield put(Actions.highlightReachChart(channelId))
}

export function* fetchInfluencerVersion2({ payload: id }) {
  try {
    const body = {
      person: {
        id: [id]
      },
      search_in: ['all_contacts']
    }
    const result = yield call(JournlistsApi.search, body)
    yield put(Actions.fetchInfluencerSuccess(result))
  } catch (error) {
    yield put(Actions.fetchInfluencerError(error))
    yield put(exception(error))
  }
}

export function* addInfluencerToContacts() {
  const i18n = yield select(Selectors.getI18n)
  const influencer = yield select(Selectors.getAddToContactsInfluencer)
  const contactData = yield select(Selectors.getAddToContactsDialogContactData)
  const distributionListFormData = yield select(Selectors.getCmDaDistributionListFormData)

  const body = {
    identity_ids: [contactData.get('identityId')]
  }

  // we now use the import route which allows us to add journalists to distribution lists
  // so it's not necessary to have a separate action for this
  if (distributionListFormData) {
    const distributionListIds = distributionListFormData.get('selectedLists')
    const newDistributionListNames = distributionListFormData.get('newListNames')

    if (!distributionListIds.isEmpty()) {
      body.distribution_list_ids = distributionListIds.toJS()
    }

    if (!newDistributionListNames.isEmpty()) {
      body.new_distribution_lists = newDistributionListNames.toJS()
    }
  }

  try {
    const result = yield call(Api.addInfluencerToContacts, influencer.get('id'), body)
    yield put(Actions.addInfluencerToContactsSuccess(result))
    yield put(ContactManagementActions.updateDistributionListFormData({ key: 'selectedLists', value: [] }))
    yield put(ContactManagementActions.updateDistributionListFormData({ key: 'newListNames', value: [] }))
    yield put(AppActions.showAppSnackbarMessage({
      text: i18n.get('contact_added'),
      variant: 'success'
    }))
  } catch (error) {
    yield put(Actions.addInfluencerToContactsError(error))
    yield put(exception(error))
    yield put(AppActions.showAppSnackbarMessage({
      text: error.response.message,
      variant: 'error'
    }))
  }
}

export function* watchFetchInfluencers() {
  yield takeLatest(Actions.influencersRequestStart, fetchInfluencers)
}

export function* watchFetchMoreInfluencers() {
  yield takeEvery(Actions.moreInfluencersRequestStart, fetchMoreInfluencers)
}

export function* watchExportInfluencersXlsx() {
  yield takeEvery(Actions.exportInfluencersXlsxStart, exportInfluencersXlsx)
}

export function* watchFetchInfluencer() {
  yield takeLatest(Actions.fetchInfluencerRequestStart, fetchInfluencer)
}

export function* watchSetSortBy() {
  yield takeEvery(Actions.setSortBy, setSortBy)
}

export function* watchSearchForInfluencer() {
  yield takeLatest(Actions.searchForInfluencer, searchForInfluencer)
}

export function* watchShowDetails() {
  yield takeEvery(Actions.showInfluencerDetails, showInfluencerDetails)
}

export function* watchSelectChannel() {
  yield takeEvery(Actions.selectChannel, selectChannel)
}

export function* watchFetchInfluencerStart() {
  yield takeEvery(Actions.fetchInfluencerStart, fetchInfluencerVersion2)
}

export function* watchAddInfluencerToContacts() {
  yield takeEvery(Actions.addInfluencerToContactsStart, addInfluencerToContacts)
}

export default function* saga() {
  yield all([
    watchFetchInfluencers(),
    watchFetchMoreInfluencers(),
    watchExportInfluencersXlsx(),
    watchFetchInfluencer(),
    watchSetSortBy(),
    watchSearchForInfluencer(),
    watchShowDetails(),
    watchSelectChannel(),
    watchFetchInfluencerStart(),
    watchAddInfluencerToContacts()
  ])
}
